import { useTranslation } from 'react-i18next';
import { MoreHorizontal, Pencil, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

export interface CatalogRowActionsProps {
  /** The row's name, for the menu button's label. */
  name: string;
  onEdit: () => void;
  onDelete: () => void;
  /** Set when the row cannot be deleted (a system record): the item is disabled and says why. */
  deleteBlockedReason?: string;
}

/**
 * The ⋯ menu at the end of a catalog list row (product families, product
 * types): Edit, and Delete — disabled, with the reason on hover, for records
 * the platform relies on.
 */
export function CatalogRowActions({ name, onEdit, onDelete, deleteBlockedReason }: CatalogRowActionsProps) {
  const { t } = useTranslation();
  const blocked = !!deleteBlockedReason;

  const deleteItem = (
    <DropdownMenuItem variant="destructive" disabled={blocked} onSelect={onDelete} data-testid="catalog-row-delete">
      <Trash2 />
      {t('ADMIN_SHELL.COMMON.DELETE')}
    </DropdownMenuItem>
  );

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={t('ADMIN_CATALOG.COMMON.ROW_ACTIONS', { name })}
          data-testid="catalog-row-actions"
        >
          <MoreHorizontal />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-40">
        <DropdownMenuItem onSelect={onEdit}>
          <Pencil />
          {t('ADMIN_SHELL.COMMON.EDIT')}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {blocked ? (
          <Tooltip>
            {/* A disabled item takes no pointer events: the wrapper carries the hover. */}
            <TooltipTrigger asChild>
              <div>{deleteItem}</div>
            </TooltipTrigger>
            <TooltipContent side="left">{deleteBlockedReason}</TooltipContent>
          </Tooltip>
        ) : (
          deleteItem
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
